import React from 'react';
import Tippy from '../ReactTippy';
import { USER_ROLES } from '../../utilities/constants';
import {
  isTargetRoleId,
  roleIdToJapaneseRoleName,
  isForemanRoleId,
} from '../../utilities/roleUtility';
import { Tel } from '../Tel'; // 電話番号入力フィールド
import RequireIcon from '../RequireIcon';
import _ from 'lodash';
import { deleteControlCode } from '../../utilities/stringUtil';
import { FormList, FormListItem, FormElement } from '../CommonFormComponents';
import { messageUtility } from '../../utilities/messageUtility';
import { TrashIcon } from '../icons/TrashIcon';

/**
 * 1つのフォーム
 *
 * @param {string} value 値
 * @param {string} title タイトル
 * @param {string} inputName name
 * @param {boolean} disabled 非表示
 * @param {string} message エラー時のメッセージ
 * @param {Func} onChange onChangeのコールバック
 * @param {Func} onBlur onBlurのコールバック
 * @param {boolean} isForeman 職長かどうか
 */
const FormItem = (
  value,
  title,
  inputName,
  disabled,
  message,
  onChange,
  onBlur = () => {},
  isForeman = false
) => (
  <FormListItem
    label={title}
    required={!disabled}
    info={`${isForeman && messageUtility.getMessage('I-001-252')}`}
  >
    <FormElement validate={message}>
      <input
        type='text'
        name={inputName}
        disabled={disabled}
        value={value || ''}
        onChange={event => onChange(event)}
        onBlur={event => onBlur(event)}
      />
    </FormElement>
  </FormListItem>
);

/**
 * 元請固有の協力会社の職長だけ元請からパスワード変更可能にする
 *
 * @param {boolean} joiningFlag 0なら元請固有
 * @param {*} values user_role_codeが入ったもの
 * @returns パスワード変更可ならtrue
 */
const isPasswordChangeable = (joiningFlag, values) => {
  // ロール
  const userRoleCode = values.user_role_code;
  // まだ設定されてない空文字のタイミングを防ぐ
  if (userRoleCode === '') {
    return false;
  }

  // ロールが職長かどうか
  const isCorrectRole = isTargetRoleId(
    [USER_ROLES.foreman],
    parseInt(userRoleCode, 10)
  );

  // パスワード変更可能かどうか
  return joiningFlag === 0 && isCorrectRole;
};

/**
 * 対象ユーザーのロール名を表示する
 *
 * @param {*} values user_role_codeが入ったもの
 */
const dispRoleName = values => {
  // ロール
  const userRoleCode = values.user_role_code;
  // まだ設定されてない空文字のタイミングを防ぐ
  if (_.isNil(userRoleCode) || userRoleCode == 0) {
    return false;
  }

  return roleIdToJapaneseRoleName(values.user_role_code);
};

/**
 * 協力会社ユーザーフォーム
 * @param values {object} 入力値
 * @param events {func} イベントハンドラ
 * @param messages {object} バリデーションメッセージ
 * @param image {string} 画像パス
 * @param joiningFlag {number} 0なら元請固有
 * @param passwordChangeable {boolean} パスワード欄達の表示/非表示
 * @param passwordVisible {boolean} パスワードの文字の表示/非表示
 * @param readOnly {boolean} プレビュー表示
 * @param policyDescription {string} password policy description
 *
 * 画面遷移
 *  1.  協力会社一覧>>ユーザー確認>>職業行の確認ボタン>>編集
 */
export const PartnerUserForm = ({
  values,
  events,
  messages,
  image,
  joiningFlag = -1,
  passwordChangeable = false,
  passwordVisible = false,
  readOnly = false,
  policyDescription,
}) => (
  <div className='contruction-detail-view__main contruction-detail-view__main--fixed contruction-detail-view__main--fix'>
    <FormList>
      <FormListItem label='ユーザー種別'>
        <FormElement>{dispRoleName(values)}</FormElement>
      </FormListItem>
      <FormListItem label='所属会社'>
        <FormElement>{values.company_name}</FormElement>
      </FormListItem>
      {FormItem(
        values.name_sei,
        '姓',
        'name_sei',
        readOnly,
        messages.name_sei,
        event => events.set('name_sei', event.target.value),
        event => events.isAvailable(event)
      )}
      {FormItem(
        values.name_mei,
        '名',
        'name_mei',
        readOnly,
        messages.name_mei,
        event => events.set('name_mei', event.target.value),
        event => events.isAvailable(event)
      )}
      {FormItem(
        values.kana_sei,
        'せい',
        'kana_sei',
        readOnly,
        messages.kana_sei,
        event => events.set('kana_sei', event.target.value),
        event => events.isAvailable(event)
      )}
      {FormItem(
        values.kana_mei,
        'めい',
        'kana_mei',
        readOnly,
        messages.kana_mei,
        event => events.set('kana_mei', event.target.value),
        event => events.isAvailable(event)
      )}
      <FormListItem label='電話番号'>
        <Tel
          value={values.tel}
          handleOnChange={events.set}
          setKey='tel'
          readOnly={readOnly}
          error={messages.tel}
          validate={e => events.isAvailable(e, 'tel')}
        />
      </FormListItem>
      {FormItem(
        values.login_id,
        'ユーザーID',
        'login_id',
        readOnly,
        messages.login_id,
        event => events.set('login_id', event.target.value),
        event => events.isAvailable(event),
        values.user_role_code !== '' && isForemanRoleId(values.user_role_code)
      )}

      {/* readOnly=true :詳細時は非表示, パスワード変更可能かどうか */}
      {readOnly === false && isPasswordChangeable(joiningFlag, values) && (
        <React.Fragment>
          {/* パスワードを変更するチェックボックス(デフォルト：OFF) */}
          <FormListItem
            label='パスワード'
            className={`form-list__item${
              passwordChangeable ? ' form-list__item--open' : ''
            }`}
          >
            <FormElement>
              <label className='checkbox'>
                <input
                  type={'checkbox'}
                  // src/pages/PartnerUserEdit.jsx にある
                  onChange={() => events.switchPasswordChangeable()}
                />
                <i className='i-square i-3x' />
                パスワードを変更する
              </label>
            </FormElement>
          </FormListItem>
          {passwordChangeable && policyDescription && (
            <li className='form-list__item form-list__item--child form-list__item--child-password'>
              <div className='form-list__label' />
              <FormElement>※{policyDescription}</FormElement>
            </li>
          )}
          {/* パスワード */}
          {passwordChangeable && (
            <li className='form-list__item form-list__item--child'>
              <div className='form-list__label'>
                <span className='label'>
                  パスワード <RequireIcon />
                  {/* パスワードを*にするかの目のアイコン */}
                  <span className='login-label__sub'>
                    <div
                      className={`btn-password-visible${
                        passwordVisible ? '' : ' invisible'
                      }`}
                      // src/pages/PartnerUserEdit.jsx にある
                      onClick={() => events.switchPasswordVisible()}
                    />
                  </span>
                </span>
              </div>
              <FormElement validate={messages.password}>
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  name='password'
                  disabled={readOnly}
                  maxLength={32}
                  value={values.password || ''}
                  onChange={event => events.set('password', event.target.value)}
                  onBlur={event => events.isAvailable(event)}
                />
              </FormElement>
            </li>
          )}
          {/* パスワード確認 */}
          {passwordChangeable && (
            <FormListItem
              label='パスワード（再入力）'
              className='form-list__item form-list__item--child'
              required={true}
            >
              <FormElement validate={messages.password_confirmation}>
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  name='password_confirmation'
                  disabled={readOnly}
                  maxLength={32}
                  value={values.password_confirmation || ''}
                  onChange={event =>
                    events.set('password_confirmation', event.target.value)
                  }
                  onBlur={event => events.isAvailable(event)}
                />
              </FormElement>
            </FormListItem>
          )}
        </React.Fragment>
      )}
      <FormListItem
        label='メールアドレス'
        isRecommended={
          values.user_role_code !== '' && isForemanRoleId(values.user_role_code)
        }
      >
        <FormElement
          validate={messages.email}
          info={`${values.user_role_code !== '' &&
            isForemanRoleId(values.user_role_code) &&
            messageUtility.getMessage('I-001-418')}`}
        >
          <input
            type='text'
            name='email'
            className='form-list__input-large'
            disabled={readOnly}
            maxLength={255}
            value={values.email}
            onChange={event =>
              events.set('email', deleteControlCode(event.target.value))
            }
            onBlur={event => events.isAvailable(event)}
          />
        </FormElement>
      </FormListItem>
      <FormListItem label='アイコン画像'>
        <FormElement validate={messages.image}>
          <div className='form-list__img'>
            <div className='img-form-thumbnail'>
              <img src={image} />
            </div>
            <button
              type='button'
              className='btn-icon form-list__element--btn_delete'
              onClick={e => events.deleteImage(e)}
            >
              <TrashIcon className='icon--2_4x form-list__trash-icon' />
            </button>
          </div>
          <Tippy
            duration={10}
            arrow={true}
            arrowType='sharp'
            placement='bottom'
            html={renderTooltipContent()}
          >
            <label
              htmlFor='file-company-branch-image'
              className='btn input-file'
            >
              ファイル選択
              <input
                type='file'
                disabled={readOnly}
                id='file-company-branch-image'
                onChange={e => events.setImage(e)}
                accept='image/png,image/jpeg,image/gif'
              />
            </label>
          </Tippy>
        </FormElement>
      </FormListItem>
    </FormList>
  </div>
);

/**
 * Tooltip
 * @param data {string}
 */
const renderTooltipContent = () => {
  const rowStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '300px',
    maxHeight: '76px',
    color: '#FFF',
    fontSize: '10px',
    lineHeight: '18px',
    textAlign: 'left',
  };

  return (
    <React.Fragment>
      <div style={rowStyle}>
        JPEG、GIF、PNGファイルをアップロードしてください。
      </div>
    </React.Fragment>
  );
};



// WEBPACK FOOTER //
// ./src/components/forms/PartnerUser.jsx